// My Grades: the student's grades on class assignments. Only grades a teacher has
// released are ever listed; a score the teacher is still drafting never reaches this page.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { MyGrade, fetchMyGrades } from '../../services/classService';
import { formatScore } from '../../utils/grading';
import {
  cardStyle,
  errorStyle,
  hintStyle,
  linkButtonStyle,
  pageStyle,
  sectionHeadingStyle,
  subtitleStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('StudentGradesPage');

const StudentGradesPage: React.FC = () => {
  const { t } = useTranslation();
  const [grades, setGrades] = useState<MyGrade[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyGrades()
      .then(page => {
        setGrades(page.items);
        setTotal(page.total);
      })
      .catch(err => {
        logger.error('Could not load grades', err);
        setError(t('myGrades.couldNotLoad'));
      });
  }, [t]);

  const loadMore = async () => {
    if (!grades) return;
    setLoadingMore(true);
    try {
      const page = await fetchMyGrades(grades.length);
      setGrades([...grades, ...page.items]);
      setTotal(page.total);
    } catch (err) {
      logger.error('Could not load more grades', err);
      setError(t('myGrades.couldNotLoad'));
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle} role="alert">{error}</div>
      </div>
    );
  }
  if (!grades) return <LoadingIndicator message={t('myGrades.loading')} />;

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 id="tour-page-title" style={titleStyle}>
          {t('myGrades.title')}
        </h1>
        <p style={subtitleStyle}>{t('myGrades.subtitle')}</p>
      </div>

      <section id="tour-page-primary-action" style={cardStyle} aria-labelledby="my-grades-heading">
        <h2 id="my-grades-heading" style={sectionHeadingStyle}>
          {t('myGrades.released', { count: total })}
        </h2>
        {grades.length === 0 ? (
          <p id="tour-page-content" style={{ ...hintStyle, margin: 0 }}>{t('myGrades.none')}</p>
        ) : (
          <ul id="tour-page-content" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {grades.map(grade => (
              <li key={grade.attempt_id} data-testid="my-grade" style={{ borderTop: `1px solid ${T.border}`, padding: '0.85rem 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <strong style={{ color: T.text }}>{grade.assignment_title}</strong>
                    <p style={{ ...hintStyle, margin: '2px 0 0' }}>
                      {[
                        grade.class_name,
                        grade.content_name,
                        t('classReview.attemptN', { attempt: grade.attempt_no }),
                        t('myGrades.releasedOn', {
                          date: new Date(grade.released_at).toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric' }),
                        }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <span style={{ fontSize: '1.15rem', fontWeight: 700, color: T.primary }}>
                    <span className="sr-only">{t('myGrades.scoreLabel')}: </span>
                    {formatScore(grade.final_score, grade.max_score)}
                  </span>
                </div>
                {grade.comment && (
                  <p style={{ fontSize: '0.88rem', margin: '8px 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    <strong>{t('myGrades.teacherComment')}:</strong> {grade.comment}
                  </p>
                )}
                {grade.roleplay_session_id && (
                  <p style={{ ...hintStyle, margin: '6px 0 0' }}>
                    <Link to={`/sessions/${grade.roleplay_session_id}`} state={{ from: '/my-grades', fromLabel: t('myGrades.title') }}>
                      {t('myGrades.openSession')}
                    </Link>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
        {grades.length < total && (
          <button type="button" style={{ ...linkButtonStyle, marginTop: 10 }} disabled={loadingMore} onClick={loadMore}>
            {t('classReview.loadMore', { shown: grades.length, total })}
          </button>
        )}
      </section>
    </div>
  );
};

export default StudentGradesPage;
